/**
 * Bordered card used for the Inherent risk and Assessment result sections of the topic risk drawer.
 * Shows a title, a status element, a description, and optionally expandable detail rows.
 */
import { ExpandLess, ExpandMore } from '@mui/icons-material'
import { Button, Collapse, Divider, Paper, Stack, Typography } from '@mui/material'
import React, { useId, useState } from 'react'
import { useIntl } from 'react-intl'

export interface CollapsibleRiskCardProps {
  title: string
  statusElement?: React.ReactNode
  description: React.ReactNode
  children?: React.ReactNode
}

export const CollapsibleRiskCard: React.FC<CollapsibleRiskCardProps> = ({
  title,
  statusElement,
  description,
  children,
}) => {
  const { formatMessage } = useIntl()
  const [isExpanded, setIsExpanded] = useState(false)
  const detailsId = useId()
  const hasDetails = Boolean(children)

  return (
    <Paper variant="outlined" sx={{ borderRadius: 2, p: 2 }}>
      <Stack spacing={1}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
          <Typography variant="subtitle1">{title}</Typography>
          {statusElement}
        </Stack>
        <Typography variant="body1" color="text.secondary">
          {description}
        </Typography>
      </Stack>

      {hasDetails && (
        <>
          <Divider sx={{ my: 2 }} />
          <Collapse in={isExpanded} unmountOnExit id={detailsId}>
            <Stack spacing={2} pb={2}>
              {children}
            </Stack>
          </Collapse>
          <Stack alignItems="center">
            <Button
              size="small"
              color="primary"
              aria-expanded={isExpanded}
              // unmountOnExit removes the region while collapsed, so only reference it when it exists.
              aria-controls={isExpanded ? detailsId : undefined}
              endIcon={isExpanded ? <ExpandLess /> : <ExpandMore />}
              onClick={() => setIsExpanded(current => !current)}
            >
              {formatMessage({ id: isExpanded ? 'topicalRisk.drawer.hideDetails' : 'topicalRisk.drawer.showDetails' })}
            </Button>
          </Stack>
        </>
      )}
    </Paper>
  )
}

export default CollapsibleRiskCard
